import { useFeatureFlags } from '@hooks/LaunchDarkly/useFeatureFlags';
import CruiseCardUserFavoriteButton from 'components/CruiseCard/CruiseCardUserFavoriteButton/CruiseCardUserFavoriteButton';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { useConfig } from 'hooks';
import { getProductViewLink } from 'lib/cruise/itinerary';
import { ItineraryVoyageType } from 'lib/graphql/codegen/cruises/graphql';
import { rumLogger } from 'lib/rum/rumLogger';
import { COMPONENT } from 'lib/ui-analytics/constants';
import { trackOnShareTooltip } from 'lib/ui-analytics/events/onShareTooltip';
import { getDatesButtonLabel, getLabelByLength, getTaggingAttrs } from 'lib/utilities/CruiseCard';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useContext, useMemo } from 'react';
import { CruiseCardProps, CruiseCardTaggingAttrs } from '../CruiseCard.types';
import CruiseCardCompareCheckbox from '../CruiseCardCompareCheckbox/CruiseCardCompareCheckbox';
import CruiseCardDeparturePort from '../CruiseCardDeparturePort/CruiseCardDeparturePort';
import CruiseCardLocationList from '../CruiseCardLocationList/CruiseCardLocationList';
import { PortListVariant } from '../CruiseCardLocationList/CruiseCardLocationList.types';
import CruiseCardPrice from '../CruiseCardPrice/CruiseCardPrice';
import CruiseCardPriceDisclosure from '../CruiseCardPriceDisclosure/CruiseCardPriceDisclosure';
import CruiseCardShip from '../CruiseCardShip/CruiseCardShip';
import CruiseCardToolbarShare from '../CruiseCardToolbarShare/CruiseCardToolbarShare';
import MapCruiseCardLabel from '../MapCruiseCard/MapCruiseCardLabel';
import {
	BadgeRow,
	RefinedCruiseCardBase,
	RefinedCruiseCardButtonsContainer,
	RefinedCruiseCardCompareSocials,
	RefinedCruiseCardDetails,
	RefinedCruiseCardImage,
	RefinedCruiseCardItineraryButton,
	RefinedCruiseCardName,
	RefinedCruiseCardPriceButton,
	RefinedCruiseCardPriceDetail,
	RefinedCruiseCardRiverBadge,
	RefinedCruiseCardShipSocials,
	RefinedCruiseCardSocials,
	RefinedCruiseCardTitle,
	RefinedCruiseCardTotalNights,
} from './RefinedCruiseCard.styles';
import { RefinedCruiseHighlightList } from './RefinedCruiseHighlightList';

function RefinedCruiseCard({ cruise, listItemIndex, listCount, onClick, setActiveVariant }: CruiseCardProps) {
	const config = useConfig();
	const { cruiseSrc, featuredSailing, itinerary, sailings, id: packageId } = cruise;
	const { highlights } = cruise.cruiseSrc;
	const { globalization, locale, qualifiersApplied, logId, promotions }: any = useContext(CruiseSearchContext);
	const {
		enableUiRebranding,
		enableComparisonFeature,
		showFavoritesIconInTheItineraryTile,
		showCruiseCardExpandedDetails,
		showCruiseCardViewItineraryCta,
		enableDataLayerUnification,
	} = useFeatureFlags();
	const {
		filtersDictionary: { labelNightsAppender },
	} = globalization?.dictionaries || {};
	const { itineraryCardsDictionary } = globalization?.dictionaries || {};
	const {
		actionViewDates,
		actionViewDate,
		actionViewItinerary,
		labelDestinationVisiting,
		labelDestinationsVisiting,
		labelLandTour,
		labelLandTours,
		labelViewPortsAndMap,
		labelRiverBadge,
		labelVisitingRivers,
	} = itineraryCardsDictionary;
	const landTours = [...itinerary.preTourCities, ...itinerary.postTourCities];
	const voyageType = cruiseSrc?.masterSailing?.itinerary?.voyageType;
	const { name, overviewImage, totalNights, ship } = itinerary;
	const taggingAttrs: CruiseCardTaggingAttrs = getTaggingAttrs(cruise, listItemIndex);

	const router = useRouter();
	const { query } = router;
	const rebrandingColor = enableUiRebranding ? 'rebranding' : '';

	const viewItineraryRedirect = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			event.stopPropagation();
			try {
				const url = getProductViewLink(`${locale.path}${itinerary.productViewLink}`, query, qualifiersApplied, config, promotions, ship);
				window.location.href = url;
			} catch (error) {
				rumLogger.error('Error in View Itinerary. Failed to redirect to itinerary details', {
					source: 'viewItineraryRedirect',
					err: `Redirect Error: ${error}`,
				});
			}
		},
		[locale.path, itinerary.productViewLink, query, qualifiersApplied, logId, ship],
	);

	const sharedEventTracking = useCallback(() => {
		if (enableDataLayerUnification) {
			const cruiseIndex = listItemIndex + 1;
			trackOnShareTooltip({ cruise: cruise.cruiseSrc, cruiseIndex, resultsLength: listCount, locale, component: COMPONENT.cruiseCard });
		}
	}, [enableDataLayerUnification, cruise, listItemIndex, listCount, locale]);

	const isLargeName = useMemo(() => {
		return name?.length ? name?.length > 40 : false;
	}, [name]);

	const initialImagesOnView = 2;
	const isOptimizedAsInitialImage = listItemIndex < initialImagesOnView;

	return (
		<RefinedCruiseCardBase {...taggingAttrs} onClick={onClick}>
			<RefinedCruiseCardImage>
				<RefinedCruiseCardTitle id={`card-title-${packageId}`}>
					<BadgeRow>
						{voyageType === ItineraryVoyageType.River && (
							<RefinedCruiseCardRiverBadge data-testid={`cruise-river-badge-${packageId}`}>{labelRiverBadge}</RefinedCruiseCardRiverBadge>
						)}
						<RefinedCruiseCardTotalNights data-testid={`cruise-duration-label-${packageId}`}>
							{`${totalNights} ${labelNightsAppender}`}
						</RefinedCruiseCardTotalNights>
					</BadgeRow>
					<RefinedCruiseCardName data-testid={`cruise-name-label-${packageId}`} isLargerName={isLargeName}>
						{name}
					</RefinedCruiseCardName>
				</RefinedCruiseCardTitle>
				<Image
					src={overviewImage.path}
					layout="fill"
					alt={overviewImage.altText}
					unoptimized={true}
					priority={isOptimizedAsInitialImage}
					loading={isOptimizedAsInitialImage ? 'eager' : 'lazy'}
				/>
			</RefinedCruiseCardImage>
			{highlights.length > 0 && <RefinedCruiseHighlightList highlights={highlights} />}
			<RefinedCruiseCardDetails>
				{enableComparisonFeature && (
					<RefinedCruiseCardCompareSocials>
						<CruiseCardCompareCheckbox cruiseSrc={cruise.cruiseSrc} index={listItemIndex} count={listCount} />
						<RefinedCruiseCardSocials onClick={(event) => event.stopPropagation()}>
							{showFavoritesIconInTheItineraryTile && (
								<CruiseCardUserFavoriteButton cruise={cruiseSrc} index={listItemIndex} count={listCount} srcComponent={COMPONENT.cruiseCard} />
							)}
							<CruiseCardToolbarShare shareUrl={itinerary.productViewLink} trackingEvent={sharedEventTracking} />
						</RefinedCruiseCardSocials>
					</RefinedCruiseCardCompareSocials>
				)}
				<RefinedCruiseCardShipSocials>
					<CruiseCardShip ship={itinerary.ship} cruise={cruiseSrc} />
					{!enableComparisonFeature && (
						<RefinedCruiseCardSocials data-testid={`add-favorite-button-${packageId}`} onClick={(event) => event.stopPropagation()}>
							{showFavoritesIconInTheItineraryTile && (
								<CruiseCardUserFavoriteButton cruise={cruiseSrc} index={listItemIndex} count={listCount} srcComponent={COMPONENT.cruiseCard} />
							)}
							<CruiseCardToolbarShare shareUrl={itinerary.productViewLink} trackingEvent={sharedEventTracking} />
						</RefinedCruiseCardSocials>
					)}
				</RefinedCruiseCardShipSocials>
				<CruiseCardDeparturePort
					packageId={packageId}
					departurePort={itinerary.departurePort}
					arrivalPort={itinerary.arrivalPort}
					variant={PortListVariant.CruiseCard}
				/>
				<CruiseCardLocationList
					packageId={packageId}
					title={getLabelByLength(itinerary.visitingPorts.length, labelDestinationVisiting, labelDestinationsVisiting)}
					locations={itinerary.visitingPorts}
					variant={PortListVariant.CruiseCard}
				/>
				<CruiseCardLocationList
					packageId={packageId}
					title={getLabelByLength(landTours.length, labelLandTour, labelLandTours)}
					locations={landTours}
					variant={PortListVariant.CruiseCard}
				/>
				<CruiseCardLocationList
					packageId={packageId}
					title={labelVisitingRivers}
					locations={itinerary.visitingRivers}
					variant={PortListVariant.CruiseCard}
				/>
				{showCruiseCardExpandedDetails && (
					<MapCruiseCardLabel packageId={packageId} labelFunction={setActiveVariant} title={`+ ${labelViewPortsAndMap}`} className="itineraryCard" />
				)}
				<RefinedCruiseCardPriceDetail>
					<CruiseCardPrice packageId={packageId} sailing={featuredSailing} />
					<RefinedCruiseCardButtonsContainer>
						<RefinedCruiseCardPriceButton
							id={`card-view-dates-button-${packageId}`}
							data-testid={`cruise-view-dates-button-${packageId}`}
							className={rebrandingColor}
							variant="contained"
						>
							{getDatesButtonLabel(sailings.length, actionViewDate, actionViewDates)}
						</RefinedCruiseCardPriceButton>
						{showCruiseCardViewItineraryCta && (
							<RefinedCruiseCardItineraryButton
								data-testid={`cruise-view-itinerary-button-${packageId}`}
								id={`card-view-itinerary-button-${packageId}`}
								variant="outlined"
								onClick={viewItineraryRedirect}
							>
								{actionViewItinerary}
							</RefinedCruiseCardItineraryButton>
						)}
					</RefinedCruiseCardButtonsContainer>
				</RefinedCruiseCardPriceDetail>
				<CruiseCardPriceDisclosure packageId={packageId} sailing={featuredSailing} />
			</RefinedCruiseCardDetails>
		</RefinedCruiseCardBase>
	);
}

export default RefinedCruiseCard;
